/**
 * Справочники и правила домена «Задачи».
 *
 * Раньше эти константы жили внутри TasksPage и TaskDetailPage,
 * продублированных в оба приложения: четыре копии одного словаря
 * статусов. Цвета задавались хэксами (#3b82f6), мимо токенов,
 * а приоритеты — эмодзи в тексте лейбла.
 */

export const TASK_STATUSES = ['open', 'in_progress', 'on_review', 'completed', 'cancelled']

export const TASK_STATUS_META = {
  open: { label: 'Открыта', tone: 'info', icon: 'plus' },
  in_progress: { label: 'В работе', tone: 'warning', icon: 'wrench' },
  on_review: { label: 'На проверке', tone: 'info', icon: 'search' },
  completed: { label: 'Завершена', tone: 'success', icon: 'check' },
  cancelled: { label: 'Отменена', tone: 'neutral', icon: 'close' },
}

export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent']

export const TASK_PRIORITY_META = {
  low: { label: 'Низкий', tone: 'success', weight: 0 },
  normal: { label: 'Обычный', tone: 'neutral', weight: 1 },
  high: { label: 'Высокий', tone: 'warning', weight: 2 },
  urgent: { label: 'Срочный', tone: 'danger', weight: 3 },
}

/** Фильтр по статусу. Пустой ключ — «все». */
export const TASK_STATUS_FILTERS = [
  { key: '', label: 'Все' },
  ...TASK_STATUSES.map((s) => ({ key: s, label: TASK_STATUS_META[s].label })),
]

/** Область выборки: все задачи, назначенные мне, созданные мной. */
export const TASK_SCOPES = [
  { key: 'all', label: 'Все' },
  { key: 'mine', label: 'Мне' },
  { key: 'created', label: 'От меня', staffOnly: true },
]

export const statusMeta = (status) => TASK_STATUS_META[status] || TASK_STATUS_META.open
export const priorityMeta = (priority) => TASK_PRIORITY_META[priority] || TASK_PRIORITY_META.normal

// ── Правила доступа ─────────────────────────────────────────
//
// Раньше проверка была разбросана по страницам в виде
// `user?.role === 'manager' || user?.role === 'admin'`.
// Одно место — одно правило.

export const isStaff = (user) => user?.role === 'admin' || user?.role === 'manager'

export const canCreateTask = (user) => isStaff(user)

export const canAssignTask = (user) => isStaff(user)

/** Взять задачу в работу может исполнитель или персонал. */
export const canStartTask = (user, task) =>
  task?.status === 'open' && (isStaff(user) || task?.assignee?.id === user?.id)

/** Отправить на проверку может только тот, кто её выполняет. */
export const canCompleteTask = (user, task) =>
  task?.status === 'in_progress' && (isStaff(user) || task?.assignee?.id === user?.id)

/** Принять или вернуть задачу может только персонал. */
export const canReviewTask = (user, task) => task?.status === 'on_review' && isStaff(user)

export const canCancelTask = (user, task) =>
  isStaff(user) && !['completed', 'cancelled'].includes(task?.status)

export const canDeleteTask = (user) => user?.role === 'admin'

/** Задача просрочена, если срок в прошлом и она ещё не закрыта. */
export function isOverdue(task) {
  if (!task?.due_at) return false
  if (['completed', 'cancelled'].includes(task.status)) return false
  return new Date(task.due_at) < new Date()
}

export const availableScopes = (user) => TASK_SCOPES.filter((s) => !s.staffOnly || isStaff(user))
